// React
import { memo, useCallback, useMemo } from "react";

// Servisni sloj
import { isMoneyKey } from "services/api/pricing/keyOptions";

// UI
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";

// Lokalne komponente
import PricingStepIntro from "ui/modules/pricing/components/PricingStepIntro/PricingStepIntro";
import PricingKeyPicker from "../PricingKeyPicker/PricingKeyPicker";

// Tipovi
import { OverrideTargetStepProps } from "../../types";

/** Kome i na čemu se daje poseban uvjet. */
const OverrideTargetStep: React.FC<OverrideTargetStepProps> = (props: OverrideTargetStepProps) => {
  const { formValues, setFormValues, errors, tenants, keyOptions } = props;

  const tenantOptions: DropdownOption[] = useMemo(
    () =>
      tenants.map((tenant) => ({
        value: tenant.id,
        text: tenant.name ?? tenant.companyName ?? tenant.id,
      })),
    [tenants]
  );

  const handleTenantChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      setFormValues((previous) => ({ ...previous, tenantId: item.value }));
    },
    [setFormValues]
  );

  /*
    Odabir ključa određuje i način unosa u sljedećem koraku. Kod dana i komada nema postotka,
    nego se upisuje nova vrijednost, pa se polje odmah puni onim što danas vrijedi. Prijelaz
    između dviju vrsta briše iznos: 14 dana ne znači isto kao 14 €.
  */
  const handleKeyChange = useCallback(
    (code: string) => {
      setFormValues((previous) => {
        const wasMoney = isMoneyKey(keyOptions.find((item) => item.code === previous.keyCode));
        const selected = keyOptions.find((item) => item.code === code);
        const isMoney = isMoneyKey(selected);

        if (isMoney) {
          return {
            ...previous,
            keyCode: code,
            adjustmentType: wasMoney ? previous.adjustmentType : "Percent",
            value: wasMoney ? previous.value : null,
          };
        }

        return {
          ...previous,
          keyCode: code,
          adjustmentType: "Absolute",
          value: selected?.rawValue ?? null,
        };
      });
    },
    [keyOptions, setFormValues]
  );

  return (
    <>
      <PricingStepIntro
        title="Klijent i ključ"
        description={
          <>
            Poseban uvjet je <strong>pojedinačna iznimka</strong> za jednog klijenta i jedan
            ključ. Ako se isti dogovor ponavlja kod više klijenata, bolja je cjenovna grupa.
          </>
        }
      />

      <div className="flex flex-col gap-6 p-4 w-full max-w-4xl mx-auto">
        <div>
          <Dropdown
            name="tenantId"
            label="Klijent"
            value={formValues.tenantId}
            options={tenantOptions}
            onChange={handleTenantChange}
            error={errors.tenantId}
            required
            width="full"
          />
        </div>

        <PricingKeyPicker
          options={keyOptions}
          value={formValues.keyCode}
          error={errors.keyCode}
          onChange={handleKeyChange}
        />

        <p className="text-sm text-slate-500">
          Na nabavne cijene se ne može dati poseban uvjet, pa ih ni nema u popisu ključeva.
        </p>
      </div>
    </>
  );
};

export default memo(OverrideTargetStep);
